/**
 * Screenlux Pricing Engine V2
 * Handles pure price logic and variant matching.
 */

window.ScreenluxEngine = {
  /**
   * Validation Rules
   */
  constraints: {
    minWidth: 650,
    maxWidth: 5000,
    minHeight: 200,
    maxHeight: 3000,
  },

  /**
   * The point at which a screen outgrows the slim cassette. Height is the
   * finished height of the screen as ordered, cassette and bottom rail
   * included — the same number that reaches the order as "Höhe (mm)".
   * The Powerups app holds the identical pair in app/lib/cassette.js; change
   * both together or the shop and the back office will quote different parts.
   */
  cassette: {
    maxSlimWidth: 3000,
    maxSlimHeight: 2100,
  },

  /**
   * Which cassette a finished screen needs. Anything past either limit takes
   * the 120 mm cassette; everything else the 85 mm one.
   * @param {number} width - mm, finished
   * @param {number} height - mm, finished
   * @returns {'large'|'slim'}
   */
  cassetteFor(width, height) {
    return (width > this.cassette.maxSlimWidth || height > this.cassette.maxSlimHeight)
      ? 'large' : 'slim';
  },

  /**
   * Validates screen dimensions.
   * @param {number} width - mm
   * @param {number} height - mm
   * @returns {object} { valid: boolean, error: string|null }
   */
  validateDimensions(width, height) {
    if (!width || !height) return { valid: false, error: 'Dimensions required' };

    const localeCode = (window.ScreenluxData && window.ScreenluxData.locale || '').toLowerCase();
    const isDE = localeCode.startsWith('de');
    const isNO = localeCode.startsWith('no') || localeCode.startsWith('nb') || (window.ScreenluxData && window.ScreenluxData.config && window.ScreenluxData.config.currencyCode === 'NOK');

    if (width < this.constraints.minWidth) {
      const errorMsg = isDE 
        ? `Mindestbreite beträgt ${this.constraints.minWidth} mm`
        : (isNO ? `Min. bredde er ${this.constraints.minWidth} mm` : `Min width is ${this.constraints.minWidth}mm`);
      return { valid: false, error: errorMsg };
    }
    if (width > this.constraints.maxWidth) {
      const errorMsg = isDE 
        ? `Maximale Breite beträgt ${this.constraints.maxWidth} mm`
        : (isNO ? `Maks. bredde er ${this.constraints.maxWidth} mm` : `Max width is ${this.constraints.maxWidth}mm`);
      return { valid: false, error: errorMsg };
    }

    if (height < this.constraints.minHeight) {
      const errorMsg = isDE 
        ? `Mindesthöhe beträgt ${this.constraints.minHeight} mm`
        : (isNO ? `Min. høyde er ${this.constraints.minHeight} mm` : `Min height is ${this.constraints.minHeight}mm`);
      return { valid: false, error: errorMsg };
    }
    if (height > this.constraints.maxHeight) {
      const errorMsg = isDE 
        ? `Maximale Höhe beträgt ${this.constraints.maxHeight} mm`
        : (isNO ? `Maks. høyde er ${this.constraints.maxHeight} mm` : `Max height is ${this.constraints.maxHeight}mm`);
      return { valid: false, error: errorMsg };
    }

    return { valid: true, error: null };
  },

  /**
   * Calculates the raw price for a single screen configuration.
   * @param {object} config - { width, height, solar, cassette }
   * @param {object} rules - pricing rules/constants
   * @returns {number} price in Euro cents
   */
  calculateScreenPrice(config, rules) {
    // 0. Base Validation Check
    const validation = this.validateDimensions(config.width, config.height);
    if (!validation.valid) {
      const allowOversize = rules && rules.allowOversize;
      const isOversizeOnly = config.width >= this.constraints.minWidth && config.height >= this.constraints.minHeight;
      if (allowOversize && isOversizeOnly) {
        // bypass validation and calculate price
      } else {
        return 0;
      }
    }

    // 1. Base Price
    let total = 55000; // 550€

    // Calculate dimensions
    const widthM = config.width / 1000;
    const calcHeight = Math.max(600, config.height);
    const heightM = calcHeight / 1000;
    const sqm = widthM * heightM;

    // 2. Fabric Cost
    const fabricPricePerSqm = config.fabricType === 'blackout' ? 10000 : 10000;
    total += Math.round(sqm * fabricPricePerSqm);

    // 3. Size Cost (Width and Height)
    const widthPrice = 11500; // €115.00 per meter
    const heightPrice = 7000; // €70.00 per meter
    total += Math.round(widthM * widthPrice + heightM * heightPrice);

    // 4. Cassette Type Cost
    if (config.cassetteSize === 'large') {
      total += 4600;
    }

    // 5. Motor Surcharge
    if (config.motor === 'solar') {
      total += 13800;
    }

    // 5.5 Brackets (NO Market Only)
    if (rules && rules.currencyCode === 'NOK' && config.mountingBracket) {
      // Whole multiples of the €23 variant step, so the bracket adds the same
      // NOK at every screen size (4 and 6 steps: 759 / 1 139 kr after the 40 %).
      if (config.mountingBracket === 'fixed') {
        total += 9200; // €92 (was €115)
      } else if (config.mountingBracket === 'flexible') {
        total += 13800; // €138 (was €181)
      }
    }

    // 6. Currency Conversion
    // The NO uplift is deliberately a separate factor from the exchange rate: the
    // rate is FX, the multiplier is a pricing decision. Their product must equal
    // the multiplier used to build the Norway catalog's compare-at ladder, because
    // matchVariant snaps this figure against compare_at_price.
    if (rules && rules.currencyCode === 'NOK') {
      const nokRate = parseFloat(rules.exchange_rate_nok) || 11;
      const noMultiplier = parseFloat(rules.no_price_multiplier) || 1;
      total = Math.round(total * nokRate * noMultiplier);
    }

    return total;
  },

  /**
   * Calculates the estimated installation price.
   * @param {Array} screens - list of screen objects
   * @param {object} config - config object
   * @returns {number} estimated installation price in cents
   */
  getEstimatedInstallationPrice(screens, config) {
    if (!screens || screens.length === 0) return 0;
    const hasWired = screens.some((s) => s.motor !== 'solar');
    const numScreens = screens.length;
    
    const cfg = config || window.ScreenluxData?.config || {};
    let wiredBase, wiredExtra, solarBase, solarExtra;

    if (cfg.currencyCode === 'NOK') {
      wiredBase = cfg.install_wired_base_nok || 799900;
      wiredExtra = cfg.install_wired_extra_nok || 280000;
      solarBase = cfg.install_solar_base_nok || 340000;
      solarExtra = cfg.install_solar_extra_nok || 115000;
    } else {
      wiredBase = cfg.install_wired_base_eur || 69900;
      wiredExtra = cfg.install_wired_extra_eur || 25000;
      solarBase = cfg.install_solar_base_eur || 29900;
      solarExtra = cfg.install_solar_extra_eur || 10000;
    }

    if (hasWired) {
      return wiredBase + (numScreens - 1) * wiredExtra;
    } else {
      return solarBase + numScreens * solarExtra;
    }
  },

  /**
   * Snaps a raw price (cost) to a matching variant by SKU.
   * @param {number} rawPrice - calculated cost in cents
   * @param {Array} variants - list of {id, price, sku} from ScreenluxData.screens
   * @returns {object|null} matching variant object
   */
  matchVariant(rawPrice, variants) {
    const targetCost = rawPrice;

    // First try exact match on compare_at_price
    const match = variants.find((v) => v.compare_at_price === targetCost);
    if (match) return match;

    // Filter valid variants that have a number compare_at_price
    const validVariants = variants.filter((v) => typeof v.compare_at_price === 'number');

    // Find all variants where compare_at_price >= targetCost (snap upwards)
    const equalOrHigherVariants = validVariants.filter((v) => v.compare_at_price >= targetCost);

    if (equalOrHigherVariants.length > 0) {
      // Find the one with the smallest compare_at_price among the higher variants
      return equalOrHigherVariants.reduce((prev, curr) => {
        return curr.compare_at_price < prev.compare_at_price ? curr : prev;
      });
    }

    // Fallback: if calculated price is higher than all variants, snap to the highest available variant
    if (validVariants.length > 0) {
      return validVariants.reduce((prev, curr) => {
        return curr.compare_at_price > prev.compare_at_price ? curr : prev;
      });
    }

    // Default fallback
    return variants[0] || null;
  },

  /**
   * Generates the Cart API Items payload for a whole session
   * @param {object} state - Global state { screens: [], installationType, ... }
   * @param {object} data - ScreenluxData global
   */
  generateCartPayload(state, data) {
    const items = [];

    // Deposit ("Anzahlung"): when the customer chose to pay a percentage now,
    // every line carries the deposit selling plan so checkout charges only
    // that share and vaults the payment method for the balance. Accessories
    // use their own product's plan id (0 when the product has none).
    const depositPlanId = (state.depositEnabled !== false && data.config && data.config.depositPlan && data.config.depositPlan.id)
      ? data.config.depositPlan.id : null;
    const planFor = (list, variantId) => {
      if (!depositPlanId) return {};
      const p = (list || []).find((x) => x.id == variantId);
      return p && p.deposit_plan_id ? { selling_plan: p.deposit_plan_id } : {};
    };

    // Helper to find title by ID
    const findTitle = (list, id) => {
      const item = (list || []).find((x) => x.id == id);
      return item ? item.title : id;
    };

    // Fabric colour title, using the blackout-specific label (e.g. Edelweiß -> Weiß)
    // when the blackout fabric is selected, so the cart line matches the storefront.
    const findFabricColorTitle = (screen) => {
      const item = (data.fabricColors || []).find((x) => x.id == screen.fabricColor);
      if (!item) return screen.fabricColor;
      return screen.fabricType === 'blackout' && item.blackoutTitle ? item.blackoutTitle : item.title;
    };

    // 1. Screens
    state.screens.forEach((screen, index) => {
      // 1. Screens
      const rawPrice = this.calculateScreenPrice(screen, data.config);
      const variant = this.matchVariant(rawPrice, data.screens);

      const isNO = data.config && data.config.currencyCode === 'NOK';
      const rawMount = (screen.rawMountType || '').toLowerCase();
      const isRecess = rawMount.includes('recess') || rawMount.includes('laibung') || rawMount.includes('innvendig') || rawMount.includes('i_nisje');
      const rawFasten = (screen.rawFasteningType || '').toLowerCase();
      const isSideways = rawFasten.includes('sideways') || rawFasten.includes('seitlich') || rawFasten.includes('sideveis');
      const selectedFixingId = screen.screenFixing || ((isRecess && isSideways) ? 'sideways' : 'backwards');

      let fixingVal = '';
      if (selectedFixingId === 'sideways') {
        fixingVal = (window.ScreenluxTranslations && window.ScreenluxTranslations.options && window.ScreenluxTranslations.options.screenFixingSideways) || (isNO ? 'Sideveis' : 'Seitlich');
      } else if (selectedFixingId === 'left-back-right-side') {
        fixingVal = (window.ScreenluxTranslations && window.ScreenluxTranslations.options && window.ScreenluxTranslations.options.screenFixingLeftBackRightSide) || (isNO ? 'Venstre: bakover | Høyre: sideveis' : 'Links: hinten | Rechts: seitlich');
      } else if (selectedFixingId === 'right-back-left-side') {
        fixingVal = (window.ScreenluxTranslations && window.ScreenluxTranslations.options && window.ScreenluxTranslations.options.screenFixingRightBackLeftSide) || (isNO ? 'Høyre: bakover | Venstre: sideveis' : 'Rechts: hinten | Links: seitlich');
      } else {
        fixingVal = (window.ScreenluxTranslations && window.ScreenluxTranslations.options && window.ScreenluxTranslations.options.screenFixingBackwards) || (isNO ? 'Bakover' : 'Nach hinten');
      }

      const fixingKey = (window.ScreenluxTranslations && window.ScreenluxTranslations.options && window.ScreenluxTranslations.options.screenFixing) || (isNO ? 'Screen-innfestning' : 'Screen-Befestigung');

      items.push({
        id: variant.id,
        quantity: 1,
        ...(depositPlanId ? { selling_plan: depositPlanId } : {}),
        properties: {
          '_Screen ID': index + 1,
          '_hide_variant': 'true',
          [(window.ScreenluxTranslations && window.ScreenluxTranslations.options && window.ScreenluxTranslations.options.reference) || 'Referenz']: screen.name || `${(window.ScreenluxTranslations && window.ScreenluxTranslations.options && window.ScreenluxTranslations.options.screenPrefix) || 'Screen'} ${index + 1}`,
          [(window.ScreenluxTranslations && window.ScreenluxTranslations.dimensions && window.ScreenluxTranslations.dimensions.width) || 'Breite']: `${screen.width}`,
          [(window.ScreenluxTranslations && window.ScreenluxTranslations.dimensions && window.ScreenluxTranslations.dimensions.height) || 'H\u00f6he']: `${screen.height}`,
          [(window.ScreenluxTranslations && window.ScreenluxTranslations.options && window.ScreenluxTranslations.options.frameColor) || 'Gestellfarbe']: findTitle(data.frameColors, screen.frameColor),
          [(window.ScreenluxTranslations && window.ScreenluxTranslations.options && window.ScreenluxTranslations.options.fabricColor) || 'Stofffarbe']: findFabricColorTitle(screen),
          [(window.ScreenluxTranslations && window.ScreenluxTranslations.options && window.ScreenluxTranslations.options.fabricTransparency) || 'Stoff']: findTitle(data.fabrics, screen.fabricType),
          [(window.ScreenluxTranslations && window.ScreenluxTranslations.options && window.ScreenluxTranslations.options.cassetteSize) || 'Kassettengr\u00f6\u00dfe']: findTitle(data.cassetteSizes, screen.cassetteSize),
          [(window.ScreenluxTranslations && window.ScreenluxTranslations.options && window.ScreenluxTranslations.options.motor) || 'Antrieb']: findTitle(data.motorOptions, screen.motor),
          ...(screen.motor === 'wired' && screen.cableExit ? { [(window.ScreenluxTranslations && window.ScreenluxTranslations.options && window.ScreenluxTranslations.options.cableExit) || 'Kabelausgang']: findTitle(data.cableExitOptions, screen.cableExit) } : {}),
          ...(screen.motor === 'wired' && screen.motorPlacement ? { [(window.ScreenluxTranslations && window.ScreenluxTranslations.options && window.ScreenluxTranslations.options.motor_placement) || 'Motor placement']: findTitle(data.motorPlacementOptions, screen.motorPlacement) } : {}),
          ...((data.config && data.config.currencyCode === 'NOK' && screen.mountingBracket && screen.mountingBracket !== 'none') ? { 
            [(window.ScreenluxTranslations && window.ScreenluxTranslations.options && window.ScreenluxTranslations.options.mountingBrackets) || 'Monteringsbraketter']: `${screen.height > 2700 ? 8 : 6}x ${findTitle(data.bracketOptions, screen.mountingBracket).toLowerCase()}`
          } : {}),
          [fixingKey]: fixingVal,
          ...(screen.measurementGuarantee ? {
            [(window.ScreenluxTranslations && window.ScreenluxTranslations.options && window.ScreenluxTranslations.options.measurementGuarantee) || '100% Passgarantie']: (window.ScreenluxTranslations && window.ScreenluxTranslations.options && window.ScreenluxTranslations.options.guaranteeActiveValue) || 'Aktiv'
          } : {}),
          ...(screen.rawMountType ? { '_Raw Mount Type': screen.rawMountType } : {}),
          ...(screen.rawType ? { '_Raw Window Type': screen.rawType } : {}),
          ...(screen.rawWidthTop ? { '_Raw Width Top': `${screen.rawWidthTop}` } : {}),
          ...(screen.rawWidthMiddle ? { '_Raw Width Middle': `${screen.rawWidthMiddle}` } : {}),
          ...(screen.rawWidthBottom ? { '_Raw Width Bottom': `${screen.rawWidthBottom}` } : {}),
          ...(screen.rawHeightLeft ? { '_Raw Height Left': `${screen.rawHeightLeft}` } : {}),
          ...(screen.rawHeightRight ? { '_Raw Height Right': `${screen.rawHeightRight}` } : {}),
          ...(screen.rawWindowOpeningOut ? { '_Raw Window Opening Out': screen.rawWindowOpeningOut } : {}),
          ...(screen.rawTopToWaterboardHeight ? { '_Raw Space Top Window to Waterboard': `${screen.rawTopToWaterboardHeight}` } : {}),
          ...(screen.rawDoorOpening ? { '_Raw Door Opening': screen.rawDoorOpening } : {}),
          ...(screen.rawHasWaterboard ? { '_Raw Has Waterboard': screen.rawHasWaterboard } : {}),
          ...(screen.rawHasHandle ? { '_Raw Has Handle': screen.rawHasHandle } : {}),
          ...(screen.rawWaterboardDistance ? { '_Raw Waterboard Distance': `${screen.rawWaterboardDistance}` } : {}),
          ...(screen.rawHandleDistance ? { '_Raw Handle Distance': `${screen.rawHandleDistance}` } : {}),
          ...(screen.rawHasRoofOverhang ? { '_Raw Has Roof Overhang': screen.rawHasRoofOverhang } : {}),
          ...(screen.rawRoofOverhangDistance ? { '_Raw Roof Overhang Distance': `${screen.rawRoofOverhangDistance}` } : {}),
          ...(screen.rawIsEstimate ? { '_Raw Is Estimate': 'true' } : {})
        },
      });
    });

    // 2. Installation Service (Professional Only)
    if (state.installationType === 'professional') {
      const hasWired = state.screens.some((s) => s.motor !== 'solar');
      const targetTitle = hasWired ? 'Wired' : 'Solar';

      // 2.1 First try to find the "Kostenlose angebot für montage" (free offer/assembly) product
      const freeKeywords = ['kostenlose', 'kostenloses', 'angebot', 'gratis', 'befaring', 'monteringstilbud', 'tilbud'];
      let serviceVariant = (data.services || []).find((v) => {
        const pTitle = (v.product_title || '').toLowerCase();
        const vTitle = (v.title || '').toLowerCase();
        return freeKeywords.some(kw => pTitle.includes(kw) || vTitle.includes(kw));
      });

      // 2.2 Fallback to standard Wired/Solar variants
      if (!serviceVariant) {
        serviceVariant = (data.services || []).find((v) => (v.title || '').includes(targetTitle));
      }

      // 2.3 Fallback to first available service variant
      if (!serviceVariant && data.services && data.services.length > 0) {
        serviceVariant = data.services[0];
      }

      if (serviceVariant) {
        const isNO = (data.config && data.config.currencyCode === 'NOK');
        const currencySymbol = (data.config && data.config.currencySymbol) || (isNO ? 'kr' : '€');
        
        const numWindows = state.screens.length;
        const rawPrice = this.getEstimatedInstallationPrice(state.screens, data.config);
        const formattedPrice = `~ ${(rawPrice / 100).toFixed(0)} ${currencySymbol}`;

        const propEstimatedPriceKey = (window.ScreenluxTranslations && window.ScreenluxTranslations.installationProfessional && window.ScreenluxTranslations.installationProfessional.estimatedTotalPrice) || (isNO ? 'Estimert totalpris' : 'Geschätzter Gesamtpreis');
        const propNumWindowsKey = (window.ScreenluxTranslations && window.ScreenluxTranslations.installationProfessional && window.ScreenluxTranslations.installationProfessional.numberOfWindows) || (isNO ? 'Antall vinduer' : 'Anzahl der Fenster');

        items.push({
          id: serviceVariant.id,
          quantity: 1,
          ...planFor(data.services, serviceVariant.id),
          properties: {
            [propEstimatedPriceKey]: formattedPrice,
            [propNumWindowsKey]: `${numWindows}`
          }
        });
      }
    }

    // 3. Brackets (Removed - now handled per screen)

    // 4. Steuerung
    Object.entries(state.steuerung || {}).forEach(([id, qty]) => {
      if (qty > 0) {
        items.push({
          id: parseInt(id),
          quantity: qty,
          ...planFor(data.steuerung, parseInt(id)),
        });
      }
    });

    // 5. Automatisierung
    Object.entries(state.automatisierung || {}).forEach(([id, qty]) => {
      if (qty > 0) {
        items.push({
          id: parseInt(id),
          quantity: qty,
          ...planFor(data.automatisierung, parseInt(id)),
        });
      }
    });

    return { items };
  },
};
